/**
 * @fileoverview SWR Global Configuration
 * 
 * Centralized SWR configuration for the entire application.
 * Includes default settings, error handling, and revalidation strategies.
 * 
 * @module hooks/swr/swrConfig
 */

import { toast } from '@/utils/toast';

/**
 * Global SWR configuration options
 */
export const swrConfig = {
  // Revalidation settings
  revalidateOnFocus: false,          // Don't refetch when window regains focus
  revalidateOnReconnect: true,       // Refetch when network reconnects
  revalidateIfStale: true,           // Revalidate if data is stale
  
  // Cache settings
  dedupingInterval: 2000,            // Dedupe requests within 2 seconds
  
  // Error retry settings
  errorRetryCount: 3,                // Retry 3 times on error
  errorRetryInterval: 1000,          // Wait 1 second between retries
  
  // Loading state
  keepPreviousData: true,            // Keep showing old data while revalidating
  
  // Global error handler
  onError: (error, key) => {
    // Don't show toast for aborted requests
    if (error?.name === 'AbortError') return;
    
    // Handle 401 errors (unauthorized)
    if (error?.response?.status === 401) {
      // Let the API client handle this
      return;
    }
    
    // Log error for debugging
    console.error(`[SWR Error] Key: ${key}`, error);
    
    // Show user-friendly error toast
    const message = error?.response?.data?.message || error?.message || 'An error occurred';
    toast.error(message);
  },
  
  // Global success handler (optional)
  onSuccess: (data, key) => {
    // Can be used for logging or analytics
  },
};

/**
 * Cache time constants (in milliseconds)
 */
export const CACHE_TIME = {
  SHORT: 30 * 1000,          // 30 seconds - for frequently changing data
  MEDIUM: 5 * 60 * 1000,     // 5 minutes - for moderately changing data
  LONG: 30 * 60 * 1000,      // 30 minutes - for rarely changing data
  STATIC: 60 * 60 * 1000,    // 1 hour - for static data
};

/**
 * SWR key constants for consistent cache key usage
 */
export const SWR_KEYS = {
  // Orders
  ORDERS: 'orders',
  ORDERS_PAID: 'orders-paid',
  ORDER_DETAIL: (id) => `order-${id}`,
  
  // Menu
  MENUS: 'menus',
  MENU_DETAIL: (id) => `menu-${id}`,
  CATEGORIES: 'categories',
  CATEGORY_DETAIL: (id) => `category-${id}`,
  ITEMS: 'items',
  ITEM_DETAIL: (id) => `item-${id}`,
  OPTIONS: 'options',
  
  // Tables
  TABLES: 'tables',
  TABLE_DETAIL: (id) => `table-${id}`,
  SECTORS: 'sectors',
  SECTOR_DETAIL: (id) => `sector-${id}`,
  
  // Employees
  EMPLOYEES: 'employees',
  EMPLOYEE_DETAIL: (id) => `employee-${id}`,
  EMPLOYEE_SCHEDULES: (id) => `employee-schedules-${id}`,
  
  // Bookings
  BOOKINGS: 'bookings',
  BOOKING_DETAIL: (id) => `booking-${id}`,
  
  // Restaurant
  RESTAURANT: 'restaurant',
  RESTAURANT_SETTINGS: 'restaurant-settings',
  
  // Dashboard
  DASHBOARD: 'dashboard',
  
  // Inventory
  INVENTORY: 'inventory',
};

export default swrConfig;
